import React, { useMemo, useState } from "react";
import {
    Search,
    Filter,
    RotateCcw,
    Eye,
    MapPin,
    CalendarDays,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";

import "../styles/Quotes.css";

const quoteData = [
    {
        id: "Q000245",
        customer: "Kumar",
        phone: "9876543210",
        pickup: "Erode",
        delivery: "Chennai",
        date: "10 Oct 2026",
        moveType: "Residential Move",
        moveSubType: "Inter City",
        amount: 25000,
        status: "NEW",
    },
    {
        id: "Q000244",
        customer: "Rajesh",
        phone: "9876543211",
        pickup: "Coimbatore",
        delivery: "Bangalore",
        date: "12 Oct 2026",
        moveType: "Residential Move",
        moveSubType: "Outer City",
        amount: 28500,
        status: "CONTACTED",
    },
    {
        id: "Q000243",
        customer: "Prakash",
        phone: "9876543212",
        pickup: "Chennai",
        delivery: "Madurai",
        date: "14 Oct 2026",
        moveType: "Office Move",
        moveSubType: "Inter City",
        amount: 42000,
        status: "QUOTATION_SENT",
    },
    {
        id: "Q000242",
        customer: "Suresh",
        phone: "9876543213",
        pickup: "Salem",
        delivery: "Erode",
        date: "15 Oct 2026",
        moveType: "Residential Move",
        moveSubType: "Part Load",
        amount: 14500,
        status: "IN_PROGRESS",
    },
    {
        id: "Q000241",
        customer: "Arun",
        phone: "9876543214",
        pickup: "Tiruppur",
        delivery: "Chennai",
        date: "18 Oct 2026",
        moveType: "Residential Move",
        moveSubType: "Inter City",
        amount: 32000,
        status: "COMPLETED",
    },
    {
        id: "Q000240",
        customer: "Mohan",
        phone: "9876543215",
        pickup: "Erode",
        delivery: "Salem",
        date: "20 Oct 2026",
        moveType: "Vehicle Transport",
        moveSubType: "Part Load",
        amount: 12000,
        status: "NEW",
    },
    {
        id: "Q000239",
        customer: "Vijay",
        phone: "9876543216",
        pickup: "Bangalore",
        delivery: "Chennai",
        date: "21 Oct 2026",
        moveType: "Residential Move",
        moveSubType: "Inter City",
        amount: 38000,
        status: "CANCELLED",
    },
    {
        id: "Q000238",
        customer: "Senthil",
        phone: "9876543217",
        pickup: "Chennai",
        delivery: "Coimbatore",
        date: "23 Oct 2026",
        moveType: "Office Move",
        moveSubType: "Outer City",
        amount: 46000,
        status: "QUOTATION_SENT",
    },
    {
        id: "Q000237",
        customer: "Manoj",
        phone: "9876543218",
        pickup: "Madurai",
        delivery: "Erode",
        date: "25 Oct 2026",
        moveType: "Residential Move",
        moveSubType: "Part Load",
        amount: 13500,
        status: "CONTACTED",
    },
    {
        id: "Q000236",
        customer: "Dinesh",
        phone: "9876543219",
        pickup: "Salem",
        delivery: "Chennai",
        date: "26 Oct 2026",
        moveType: "Residential Move",
        moveSubType: "Inter City",
        amount: 27500,
        status: "IN_PROGRESS",
    },
];

const statusOptions = [
    "ALL",
    "NEW",
    "CONTACTED",
    "QUOTATION_SENT",
    "IN_PROGRESS",
    "COMPLETED",
    "CANCELLED",
];

const getStatusClass = (status) => {
    switch (status) {
        case "NEW":
            return "quote-status-new";

        case "CONTACTED":
            return "quote-status-contacted";

        case "QUOTATION_SENT":
            return "quote-status-quotation";

        case "IN_PROGRESS":
            return "quote-status-progress";

        case "COMPLETED":
            return "quote-status-completed";

        case "CANCELLED":
            return "quote-status-cancelled";

        default:
            return "";
    }
};

const formatStatus = (status) => {
    return status
        .toLowerCase()
        .split("_")
        .map(
            (word) =>
                word.charAt(0).toUpperCase() + word.slice(1)
        )
        .join(" ");
};

const formatAmount = (amount) => {
    return `₹${amount.toLocaleString("en-IN")}`;
};

export default function Quotes() {
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("ALL");
    const [dateFilter, setDateFilter] = useState("");
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const filteredQuotes = useMemo(() => {
        return quoteData.filter((quote) => {

            const searchValue = search
                .toLowerCase()
                .trim();

            const matchesSearch =
                !searchValue ||
                quote.id.toLowerCase().includes(searchValue) ||
                quote.customer.toLowerCase().includes(searchValue) ||
                quote.phone.includes(searchValue) ||
                quote.pickup.toLowerCase().includes(searchValue) ||
                quote.delivery.toLowerCase().includes(searchValue);

            const matchesStatus =
                status === "ALL" ||
                quote.status === status;

            const matchesDate =
                !dateFilter ||
                quote.date.includes(dateFilter);

            return (
                matchesSearch &&
                matchesStatus &&
                matchesDate
            );
        });
    }, [search, status, dateFilter]);

    const totalPages = Math.max(
        1,
        Math.ceil(
            filteredQuotes.length / rowsPerPage
        )
    );

    const currentPage = Math.min(
        page,
        totalPages
    );

    const paginatedQuotes = filteredQuotes.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const handleSearch = (value) => {
        setSearch(value);
        setPage(1);
    };

    const handleStatus = (value) => {
        setStatus(value);
        setPage(1);
    };

    const handleClear = () => {
        setSearch("");
        setStatus("ALL");
        setDateFilter("");
        setPage(1);
    };

    return (
        <div className="quotes-page">

            {/* Page Header */}
            <div className="quotes-page-header">

                <div>
                    <h1>Quotes</h1>

                    <p>
                        Manage and track all moving quote requests.
                    </p>
                </div>

                <div className="quotes-count">
                    <strong>{filteredQuotes.length}</strong>
                    <span>Total Quotes</span>
                </div>

            </div>


            {/* Filters */}
            <div className="quotes-filter-card">

                <div className="filter-search">

                    <Search size={17} />

                    <input
                        type="text"
                        placeholder="Search quote ID, customer, mobile or location..."
                        value={search}
                        onChange={(e) =>
                            handleSearch(e.target.value)
                        }
                    />

                </div>


                <div className="filter-item">

                    <Filter size={15} />

                    <select
                        value={status}
                        onChange={(e) =>
                            handleStatus(e.target.value)
                        }
                    >
                        {statusOptions.map((item) => (
                            <option
                                key={item}
                                value={item}
                            >
                                {item === "ALL"
                                    ? "All Status"
                                    : formatStatus(item)}
                            </option>
                        ))}
                    </select>

                </div>


                <div className="filter-item date-filter">

                    <CalendarDays size={15} />

                    <input
                        type="text"
                        placeholder="Search date"
                        value={dateFilter}
                        onChange={(e) => {
                            setDateFilter(
                                e.target.value
                            );
                            setPage(1);
                        }}
                    />

                </div>


                <button
                    type="button"
                    className="clear-filter-btn"
                    onClick={handleClear}
                >
                    <RotateCcw size={15} />
                    Clear
                </button>

            </div>


            {/* Table Card */}
            <div className="quotes-table-card">

                <div className="quotes-table-header">

                    <div>
                        <h3>Quote Requests</h3>

                        <p>
                            Showing {paginatedQuotes.length} of{" "}
                            {filteredQuotes.length} quotes
                        </p>
                    </div>

                </div>


                {/* Desktop Table */}
                <div className="quotes-table-wrapper">

                    <table className="quotes-table">

                        <thead>
                            <tr>

                                <th>QUOTE</th>

                                <th>CUSTOMER</th>

                                <th>ROUTE</th>

                                <th>MOVING DATE</th>

                                <th>MOVE TYPE</th>

                                <th>AMOUNT</th>

                                <th>STATUS</th>

                                <th>ACTION</th>

                            </tr>
                        </thead>

                        <tbody>

                            {paginatedQuotes.length > 0 ? (
                                paginatedQuotes.map((quote) => (

                                    <tr key={quote.id}>

                                        {/* Quote */}
                                        <td>
                                            <div className="quote-id-cell">
                                                <strong>
                                                    {quote.id}
                                                </strong>

                                                <span>
                                                    Quote Request
                                                </span>
                                            </div>
                                        </td>


                                        {/* Customer */}
                                        <td>
                                            <div className="customer-cell">

                                                <div className="customer-avatar">
                                                    {quote.customer
                                                        .charAt(0)
                                                        .toUpperCase()}
                                                </div>

                                                <div>
                                                    <strong>
                                                        {quote.customer}
                                                    </strong>

                                                    <span>
                                                        {quote.phone}
                                                    </span>
                                                </div>

                                            </div>
                                        </td>


                                        {/* Route */}
                                        <td>
                                            <div className="route-cell">

                                                <div>
                                                    <MapPin size={13} />
                                                    <span>
                                                        {quote.pickup}
                                                    </span>
                                                </div>

                                                <span className="route-arrow">
                                                    →
                                                </span>

                                                <div>
                                                    <MapPin size={13} />
                                                    <span>
                                                        {quote.delivery}
                                                    </span>
                                                </div>

                                            </div>
                                        </td>


                                        {/* Date */}
                                        <td>
                                            <div className="moving-date">

                                                <CalendarDays size={14} />

                                                <span>
                                                    {quote.date}
                                                </span>

                                            </div>
                                        </td>


                                        {/* Move Type */}
                                        <td>
                                            <div className="move-type-cell">

                                                <strong>
                                                    {quote.moveType}
                                                </strong>

                                                <span>
                                                    {quote.moveSubType}
                                                </span>

                                            </div>
                                        </td>


                                        {/* Amount */}
                                        <td>
                                            <strong className="quote-amount">
                                                {formatAmount(
                                                    quote.amount
                                                )}
                                            </strong>
                                        </td>


                                        {/* Status */}
                                        <td>
                                            <span
                                                className={`quote-status ${getStatusClass(
                                                    quote.status
                                                )}`}
                                            >
                                                {formatStatus(
                                                    quote.status
                                                )}
                                            </span>
                                        </td>


                                        {/* Action */}
                                        <td>
                                            <button
                                                className="quote-view-btn"
                                                title="View Quote"
                                            >
                                                <Eye size={16} />
                                            </button>
                                        </td>

                                    </tr>

                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan="8"
                                        className="no-quotes"
                                    >
                                        No quotes found
                                    </td>
                                </tr>
                            )}

                        </tbody>

                    </table>

                </div>


                {/* Mobile Cards */}
                <div className="quotes-mobile-list">

                    {paginatedQuotes.length > 0 ? (
                        paginatedQuotes.map((quote) => (

                            <div
                                className="quote-mobile-card"
                                key={quote.id}
                            >

                                <div className="mobile-card-top">

                                    <div>
                                        <strong>
                                            {quote.id}
                                        </strong>

                                        <span>
                                            {quote.date}
                                        </span>
                                    </div>

                                    <span
                                        className={`quote-status ${getStatusClass(
                                            quote.status
                                        )}`}
                                    >
                                        {formatStatus(
                                            quote.status
                                        )}
                                    </span>

                                </div>


                                <div className="mobile-customer">

                                    <div className="customer-avatar">
                                        {quote.customer
                                            .charAt(0)
                                            .toUpperCase()}
                                    </div>

                                    <div>
                                        <strong>
                                            {quote.customer}
                                        </strong>

                                        <span>
                                            {quote.phone}
                                        </span>
                                    </div>

                                </div>


                                <div className="mobile-route">

                                    <div>
                                        <small>FROM</small>
                                        <strong>
                                            {quote.pickup}
                                        </strong>
                                    </div>

                                    <span>→</span>

                                    <div>
                                        <small>TO</small>
                                        <strong>
                                            {quote.delivery}
                                        </strong>
                                    </div>

                                </div>


                                <div className="mobile-card-bottom">

                                    <div>
                                        <small>
                                            Move Type
                                        </small>

                                        <strong>
                                            {quote.moveSubType}
                                        </strong>
                                    </div>

                                    <div>
                                        <small>
                                            Amount
                                        </small>

                                        <strong>
                                            {formatAmount(
                                                quote.amount
                                            )}
                                        </strong>
                                    </div>

                                    <button className="quote-view-btn">
                                        <Eye size={16} />
                                    </button>

                                </div>

                            </div>

                        ))
                    ) : (
                        <div className="mobile-no-quotes">
                            No quotes found
                        </div>
                    )}

                </div>


                {/* Pagination */}
                <div className="quotes-pagination">

                    <div className="pagination-info">
                        Page {currentPage} of {totalPages}
                    </div>


                    <div className="pagination-controls">

                        <select
                            value={rowsPerPage}
                            onChange={(e) => {
                                setRowsPerPage(
                                    Number(e.target.value)
                                );
                                setPage(1);
                            }}
                        >
                            <option value="5">
                                5 / page
                            </option>

                            <option value="10">
                                10 / page
                            </option>

                            <option value="20">
                                20 / page
                            </option>
                        </select>


                        <button
                            disabled={currentPage === 1}
                            onClick={() =>
                                setPage(
                                    (p) =>
                                        Math.max(
                                            1,
                                            p - 1
                                        )
                                )
                            }
                        >
                            <ChevronLeft size={16} />
                        </button>


                        <span className="current-page">
                            {currentPage}
                        </span>


                        <button
                            disabled={
                                currentPage ===
                                totalPages
                            }
                            onClick={() =>
                                setPage(
                                    (p) =>
                                        Math.min(
                                            totalPages,
                                            p + 1
                                        )
                                )
                            }
                        >
                            <ChevronRight size={16} />
                        </button>

                    </div>

                </div>

            </div>

        </div>
    );
}